import {
  BookOpenCheck,
  Calendar1,
  Clock,
  Clock1,
  ExternalLink,
  FolderClosed,
  NotepadText,
} from "lucide-react"
import moment from "moment"
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { IoMdCloseCircleOutline } from "react-icons/io"
import { LuClipboardCheck } from "react-icons/lu"
import { MdOutlineCheckCircle } from "react-icons/md"
import { useNavigate, useParams } from "react-router"

import LinkWithIcon from "@/components/LinkWithIcon/LinkWithIcon"
import IconOption from "@/components/TableOptions/IconOption"
import { editIcon, linkIcon, taskIcon } from "@/utils/variables"
import { useStore } from "@/zustand/store"
import { FaArrowRight, FaPlay } from "react-icons/fa"

const attendedIcon = <MdOutlineCheckCircle size={20} color="#2bc99c" />
const absentIcon = <IoMdCloseCircleOutline size={20} color="#d0242d" />

import { MdOutlineFileUpload } from "react-icons/md"

import { MODALS } from "@/constants/modals"
import type { Course } from "@/types/courseTypes"
import type { Lecture } from "@/types/lectureTypes"
import type { Round } from "@/types/roundTypes"
import { allowAttendOnlineLecture } from "@/utils/helpers"

export default function LecturesDetailsItem({
  ele,
  course,
  round,
}: {
  ele: Lecture
  course: Course
  round: Round
}) {
  const { t, i18n } = useTranslation()
  const editModalState = useStore((state) => state.editModalState)
  const { roundId } = useParams()
  const navigate = useNavigate()

  const lectureTaskUrl =
    ele.lecture_task?.task_link || ele.lecture_task?.files_included
  const attended = ele.my_evaluation?.is_attend
  const hasDeadline = ele.lecture_task?.deadline
  const deadlinePassed =
    hasDeadline && new Date(ele.lecture_task?.deadline || "") < new Date()
  const uplaodedTask = ele.my_task_submission?.task_url
  const hasFeedback = (ele.my_task_submission?.feedbacks?.length || 0) > 0
  const todaysLecture = moment(ele.start_time).isSame(new Date(), "day")

  const allowAttend = allowAttendOnlineLecture(ele, round)

  const deadLineText = (() => {
    if (ele?.lecture_task) {
      if (ele?.lecture_task?.deadline) {
        return moment(ele?.lecture_task?.deadline).format("lll")
      } else {
        return t("enrolledCoursesPage.lecturesDetails.deadlineNotSet")
      }
    } else {
      return t("enrolledCoursesPage.lecturesDetails.taskNotSet")
    }
  })()

  function getTaskColors() {
    if (!ele?.lecture_task) {
      return "border-dark-subtle bg-body-secondary"
    } else if (uplaodedTask) {
      return "border-success bg-success-subtle"
    } else if (!deadlinePassed) {
      return "border-warning bg-warning-subtle"
    } else {
      return "border-danger bg-danger-subtle"
    }
  }

  const RowProps = todaysLecture
    ? {
        style: {
          border: "1px solid #2684dc",
          borderLeft: "8px solid #2684dc",
          background: "#dff0ffae",
        },
        className: "bg-white d-flex flex-column shadow-sm py-3 px-4 rounded-3",
      }
    : {
        className:
          "bg-white d-flex flex-column shadow-sm py-3 px-4 rounded-3 border border-light-subtle",
      }

  const taskTextClassName = !ele?.lecture_task
    ? "text-dark"
    : uplaodedTask
      ? "text-success"
      : !deadlinePassed
        ? "text-warning"
        : "text-danger"

  return (
    <Row {...RowProps}>
      {/* lecture title and attendance */}
      <Col xs={12} className="d-flex align-items-center">
        <div
          className={`border rounded-4 ${attended ? "bg-success-subtle border-success" : todaysLecture ? "bg-info-subtle border-info" : "bg-danger-subtle border-danger"} d-flex align-items-center justify-content-center gap-2`}
          style={{ height: "40px", width: "40px" }}
        >
          <OverlayTrigger
            overlay={
              <Tooltip id={`${ele?.id}-attend-tooltip`}>
                {attended
                  ? t("enrolledCoursesPage.lecturesDetails.attendance.attended")
                  : todaysLecture
                    ? t(
                        "enrolledCoursesPage.lecturesDetails.attendance.todaysLecture"
                      )
                    : t(
                        "enrolledCoursesPage.lecturesDetails.attendance.absent"
                      )}
              </Tooltip>
            }
          >
            <span style={{ scale: "1.3" }}>
              {attended ? (
                attendedIcon
              ) : todaysLecture ? (
                <Clock1 size={16} color="#2684dc" />
              ) : (
                absentIcon
              )}
            </span>
          </OverlayTrigger>
        </div>
        <div className="ms-4">
          <p
            onClick={() =>
              allowAttend &&
              editModalState(MODALS.lecture.attend, { lecture: ele, round })
            }
            style={
              allowAttend
                ? {
                    cursor: "pointer",
                    textDecoration: "underline",
                  }
                : {}
            }
            className="text-dark fs-4 fw-bold m-0"
          >
            {ele?.name}
            {round?.google_meet_link && allowAttend && (
              <ExternalLink size={16} />
            )}
          </p>
          <p
            className="text-secondary m-0 text-center"
            style={{ fontSize: "12px" }}
          >
            <Calendar1
              size={16}
              className={i18n.language === "ar" ? "ms-1" : "me-1"}
            />
            {moment(ele?.start_time).format("lll")}
          </p>
        </div>
      </Col>

      {/* lecture videos */}
      <Col
        xs={12}
        onClick={() =>
          ele?.lecture_videos?.length &&
          navigate(
            `/enrolled/round/${roundId}/lectures/${ele.id}/videos?type=${ele?.type}`
          )
        }
        className="ps-1 pe-2"
      >
        <div
          style={
            ele?.lecture_videos?.length
              ? {
                  cursor: "pointer",
                  opacity: 1,
                }
              : {
                  cursor: "not-allowed",
                  opacity: 0.6,
                }
          }
          className="mt-3 hover-container border border-info bg-info-subtle px-3 py-2 rounded-4 d-flex justify-content-between align-items-center"
        >
          <p className={`d-flex align-items-center fs-5 fw-bold py-2`}>
            <div
              style={{
                scale: "1.1",
                marginInlineEnd: "10px",
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                position: "relative",
                backgroundColor: "#0fc7ee88",
              }}
            >
              <FaPlay
                size={14}
                color="#08596aff"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "55%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            </div>
            {t("enrolledCoursesPage.lecturesDetails.lectureVideosTooltip")}
          </p>
          <FaArrowRight size={18} color="#08596aff" className="arrow-right" />
        </div>
      </Col>

      {/* task submit, degree, feedback */}
      <Row className="g-2 my-2">
        <Col xs={12} md={{ span: 4 }}>
          <div
            className={`${getTaskColors()} px-3 py-2 border rounded-4 d-flex align-items-center justify-content-between gap-3`}
          >
            <div className="d-flex align-items-start gap-2">
              <Clock
                size={22}
                className={`mt-1 ${i18n.language === "ar" ? "mx-1 " : "mx-1"} ${taskTextClassName}`}
              />
              <div>
                <p className={`${taskTextClassName} fs-5 fw-bold`}>
                  {t("enrolledCoursesPage.lecturesDetails.taskSubmitTooltip")}
                </p>
                <p className="text-secondary fw-bold fs-6">{deadLineText}</p>
              </div>
            </div>
            <div className="d-flex gap-2">
              <OverlayTrigger
                overlay={
                  lectureTaskUrl ? (
                    <Tooltip id={`${ele?.id}-task`}>
                      {t("enrolledCoursesPage.lecturesDetails.taskTooltip")}
                    </Tooltip>
                  ) : (
                    <></>
                  )
                }
              >
                <a
                  style={
                    lectureTaskUrl
                      ? { opacity: 1, cursor: "pointer" }
                      : { opacity: 0.3, cursor: "not-allowed" }
                  }
                  target="_blank"
                  rel="noreferrer"
                  href={lectureTaskUrl}
                >
                  <NotepadText className="text-dark" />
                </a>
              </OverlayTrigger>
              {!hasDeadline ? (
                <></>
              ) : uplaodedTask ? (
                <div
                  style={{ width: "fit-content" }}
                  className="d-flex align-items-center gap-4"
                >
                  <LinkWithIcon
                    href={ele?.my_task_submission?.task_url}
                    icon={<LuClipboardCheck size={22} />}
                    color={"#243B78"}
                    target={"_blank"}
                    tooltip={t(
                      "enrolledCoursesPage.lecturesDetails.mySubmissionTooltip"
                    )}
                  />
                  {!deadlinePassed && (
                    <LinkWithIcon
                      onClick={() =>
                        editModalState(MODALS.lecture.uplaodTask, ele)
                      }
                      icon={editIcon}
                      color={"#F15824"}
                    />
                  )}
                </div>
              ) : !deadlinePassed ? (
                <LinkWithIcon
                  onClick={() => editModalState(MODALS.lecture.uplaodTask, ele)}
                  icon={<MdOutlineFileUpload size={26} color="black" />}
                  color={"#F15824"}
                  tooltip={t(
                    "enrolledCoursesPage.lecturesDetails.uploadTaskTooltip"
                  )}
                />
              ) : (
                <OverlayTrigger
                  overlay={
                    <Tooltip id={`${ele?.id}-task-deadline`}>
                      {t(
                        "enrolledCoursesPage.lecturesDetails.deadlinePassedTooltip"
                      )}
                    </Tooltip>
                  }
                >
                  <div style={{ scale: "1.3" }}>{absentIcon}</div>
                </OverlayTrigger>
              )}
            </div>
          </div>
        </Col>

        <Col xs={12} md={{ span: 4 }}>
          <div className="px-3 py-2 border border-dark-subtle bg-body-secondary rounded-4 d-flex gap-1">
            <BookOpenCheck
              size={20}
              className={`mt-1 ${i18n.language === "ar" ? "ms-1" : "me-1"}`}
            />
            <div>
              <p className={`fs-5 fw-bold`}>
                {t("enrolledCoursesPage.lecturesDetails.taskDegree")}
              </p>
              <p className="text-secondary fw-bold">
                {ele?.my_task_submission?.task_degree ?? "-"} {"/ 10"}
              </p>
            </div>
          </div>
        </Col>
        {hasFeedback && (
          <Col
            xs={12}
            md={{ span: 4 }}
            onClick={() =>
              editModalState(
                MODALS.enrolledRounds.notes,
                ele?.my_task_submission
              )
            }
          >
            <div
              style={{ cursor: "pointer" }}
              className="px-3 py-3 h-100 hover-container border border-info bg-info-subtle rounded-4 d-flex justify-content-between align-items-center"
            >
              <div className="d-flex align-items-center align-item-md-start">
                <span
                  style={{
                    scale: "0.9",
                    display: "inline-block",
                    marginInlineEnd: "4px",
                  }}
                >
                  {feedbackIcon}
                </span>
                <p className={`fs-5 fw-bold`}>
                  {t("enrolledCoursesPage.lecturesDetails.taskFeedback")}
                </p>
              </div>
              <FaArrowRight
                size={18}
                color="#08596aff"
                className="arrow-right"
              />
            </div>
          </Col>
        )}
      </Row>

      <hr className="my-2 " />
      {/* extar lecture options (drive, tasks, projects, additional materials) */}
      <Col xs={12}>
        <div className="px-2 d-flex gap-3 justify-content-end">
          <div>
            {ele?.task_ids?.length && (
              <IconOption
                icon={taskIcon}
                tooltip={t(
                  "enrolledCoursesPage.lecturesDetails.lectureTasksTooltip"
                )}
                onClick={() =>
                  editModalState(MODALS.lecture.tasks, { lecture: ele, course })
                }
              />
            )}
          </div>
          <div>
            {ele?.project_ids?.length && (
              <IconOption
                icon={<FolderClosed size={16} />}
                tooltip={t(
                  "enrolledCoursesPage.lecturesDetails.lectureProjectsTooltip"
                )}
                onClick={() =>
                  editModalState(MODALS.lecture.projects, {
                    lecture: ele,
                    course,
                  })
                }
              />
            )}
          </div>
          <div>
            {ele?.content && (
              <IconOption
                icon={linkIcon}
                tooltip={t(
                  "enrolledCoursesPage.lecturesDetails.lectureContentTooltip"
                )}
                onClick={() => editModalState(MODALS.lecture.content, ele)}
              />
            )}
          </div>
          <div>
            {ele?.url && (
              <IconOption
                icon={driveSvg}
                tooltip={t("enrolledCoursesPage.lecturesDetails.driveTooltip")}
                onClick={() => window.open(ele?.url)}
              />
            )}
          </div>
        </div>
      </Col>
    </Row>
  )
}

export const feedbackIcon = (
  <svg
    width="20px"
    height="20px"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
    <g
      id="SVGRepo_tracerCarrier"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></g>
    <g id="SVGRepo_iconCarrier">
      {" "}
      <path
        d="M16 1C17.6569 1 19 2.34315 19 4C19 4.55228 18.5523 5 18 5C17.4477 5 17 4.55228 17 4C17 3.44772 16.5523 3 16 3H4C3.44772 3 3 3.44772 3 4V20C3 20.5523 3.44772 21 4 21H16C16.5523 21 17 20.5523 17 20V19C17 18.4477 17.4477 18 18 18C18.5523 18 19 18.4477 19 19V20C19 21.6569 17.6569 23 16 23H4C2.34315 23 1 21.6569 1 20V4C1 2.34315 2.34315 1 4 1H16Z"
        fill="#0F0F0F"
      ></path>{" "}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20.7991 8.20087C20.4993 7.90104 20.0132 7.90104 19.7133 8.20087L11.9166 15.9977C11.7692 16.145 11.6715 16.3348 11.6373 16.5404L11.4728 17.5272L12.4596 17.3627C12.6652 17.3285 12.855 17.2308 13.0023 17.0835L20.7991 9.28666C21.099 8.98682 21.099 8.5007 20.7991 8.20087ZM18.2991 6.78666C19.38 5.70578 21.1325 5.70577 22.2134 6.78665C23.2942 7.86754 23.2942 9.61999 22.2134 10.7009L14.4166 18.4977C13.9744 18.9398 13.4052 19.2327 12.7884 19.3355L11.8016 19.5C10.448 19.7256 9.2744 18.5521 9.50001 17.1984L9.66448 16.2116C9.76728 15.5948 10.0602 15.0256 10.5023 14.5834L18.2991 6.78666Z"
        fill="#0F0F0F"
      ></path>{" "}
      <path
        d="M5 7C5 6.44772 5.44772 6 6 6H14C14.5523 6 15 6.44772 15 7C15 7.55228 14.5523 8 14 8H6C5.44772 8 5 7.55228 5 7Z"
        fill="#0F0F0F"
      ></path>{" "}
      <path
        d="M5 11C5 10.4477 5.44772 10 6 10H10C10.5523 10 11 10.4477 11 11C11 11.5523 10.5523 12 10 12H6C5.44772 12 5 11.5523 5 11Z"
        fill="#0F0F0F"
      ></path>{" "}
      <path
        d="M5 15C5 14.4477 5.44772 14 6 14H7C7.55228 14 8 14.4477 8 15C8 15.5523 7.55228 16 7 16H6C5.44772 16 5 15.5523 5 15Z"
        fill="#0F0F0F"
      ></path>{" "}
    </g>
  </svg>
)

const driveSvg = (
  <svg
    width="40px"
    height="40px"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 11.9556C2 8.47078 2 6.7284 2.67818 5.39739C3.27473 4.22661 4.22661 3.27473 5.39739 2.67818C6.7284 2 8.47078 2 11.9556 2H20.0444C23.5292 2 25.2716 2 26.6026 2.67818C27.7734 3.27473 28.7253 4.22661 29.3218 5.39739C30 6.7284 30 8.47078 30 11.9556V20.0444C30 23.5292 30 25.2716 29.3218 26.6026C28.7253 27.7734 27.7734 28.7253 26.6026 29.3218C25.2716 30 23.5292 30 20.0444 30H11.9556C8.47078 30 6.7284 30 5.39739 29.3218C4.22661 28.7253 3.27473 27.7734 2.67818 26.6026C2 25.2716 2 23.5292 2 20.0444V11.9556Z"
      fill="white"
    />
    <path
      d="M16.0019 12.4507L12.541 6.34297C12.6559 6.22598 12.7881 6.14924 12.9203 6.09766C11.8998 6.43355 11.4315 7.57961 11.4315 7.57961L5.10895 18.7345C5.01999 19.0843 4.99528 19.4 5.0064 19.6781H11.9072L16.0019 12.4507Z"
      fill="#34A853"
    />
    <path
      d="M16.002 12.4507L20.0967 19.6781H26.9975C27.0086 19.4 26.9839 19.0843 26.8949 18.7345L20.5724 7.57961C20.5724 7.57961 20.1029 6.43355 19.0835 6.09766C19.2145 6.14924 19.3479 6.22598 19.4628 6.34297L16.002 12.4507Z"
      fill="#FBBC05"
    />
    <path
      d="M16.0019 12.4514L19.4628 6.34371C19.3479 6.22671 19.2144 6.14997 19.0835 6.09839C18.9327 6.04933 18.7709 6.01662 18.5954 6.00781H18.4125H13.5913H13.4084C13.2342 6.01536 13.0711 6.04807 12.9203 6.09839C12.7894 6.14997 12.6559 6.22671 12.541 6.34371L16.0019 12.4514Z"
      fill="#188038"
    />
    <path
      d="M11.9082 19.6782L8.48687 25.7168C8.48687 25.7168 8.3732 25.6614 8.21875 25.5469C8.70434 25.9206 9.17633 25.9998 9.17633 25.9998H22.6134C23.3547 25.9998 23.5092 25.7168 23.5092 25.7168C23.5116 25.7155 23.5129 25.7142 23.5153 25.713L20.0965 19.6782H11.9082Z"
      fill="#4285F4"
    />
    <path
      d="M11.9086 19.6782H5.00781C5.04241 20.4985 5.39826 20.9778 5.39826 20.9778L5.65773 21.4281C5.67627 21.4546 5.68739 21.4697 5.68739 21.4697L6.25205 22.461L7.51976 24.6676C7.55683 24.7569 7.60008 24.8386 7.6458 24.9166C7.66309 24.9431 7.67915 24.972 7.69769 24.9972C7.70263 25.0047 7.70757 25.0123 7.71252 25.0198C7.86944 25.2412 8.04489 25.4123 8.22034 25.5469C8.37479 25.6627 8.48847 25.7168 8.48847 25.7168L11.9086 19.6782Z"
      fill="#1967D2"
    />
    <path
      d="M20.0967 19.6782H26.9974C26.9628 20.4985 26.607 20.9778 26.607 20.9778L26.3475 21.4281C26.329 21.4546 26.3179 21.4697 26.3179 21.4697L25.7532 22.461L24.4855 24.6676C24.4484 24.7569 24.4052 24.8386 24.3595 24.9166C24.3422 24.9431 24.3261 24.972 24.3076 24.9972C24.3026 25.0047 24.2977 25.0123 24.2927 25.0198C24.1358 25.2412 23.9604 25.4123 23.7849 25.5469C23.6305 25.6627 23.5168 25.7168 23.5168 25.7168L20.0967 19.6782Z"
      fill="#EA4335"
    />
  </svg>
)
